import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { isAuthenticated, clearToken, setToken, setAuthErrorHandler } from '../services/api';
import {
  signInToFirebase, registerDeviceToken, unregisterDeviceToken,
  onForegroundMessage, subscribeToInbox,
} from '../services/notifications';

const AppContext = createContext(null);

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

function getSavedUser() {
  try {
    const raw = localStorage.getItem('gotap_user');
    if (!raw) return null;
    const { data, expiry } = JSON.parse(raw);
    if (expiry && Date.now() > expiry) {
      localStorage.removeItem('gotap_user');
      localStorage.removeItem('gotap_token');
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function AppProvider({ children }) {
  const [authed, setAuthed] = useState(() => !!getSavedUser() && isAuthenticated());
  const [user,   setUser]   = useState(() => getSavedUser());
  const [toast,  setToast]  = useState(null);
  const [inbox,  setInbox]  = useState([]);

  const login = useCallback((userData, token, remember = false) => {
    if (token) setToken(token);
    setUser(userData);
    setAuthed(true);
    if (remember) {
      const stored = { data: userData, expiry: Date.now() + THIRTY_DAYS };
      localStorage.setItem('gotap_user', JSON.stringify(stored));
    } else {
      localStorage.removeItem('gotap_user');
    }
  }, []);

  const logout = useCallback(() => {
    unregisterDeviceToken().catch(()=>{});
    clearToken();
    localStorage.removeItem('gotap_user');
    setAuthed(false);
    setUser(null);
    setInbox([]);
  }, []);

  useEffect(() => {
    setAuthErrorHandler(logout);
    return () => setAuthErrorHandler(null);
  }, [logout]);

  // Firebase sign-in + inbox subscription + FCM registration — tied to auth state
  useEffect(() => {
    if (!authed || !user?.id) return;
    let unsub = () => {};
    let unsubMsg = () => {};
    let cancelled = false;

    (async () => {
      try {
        await signInToFirebase();
        if (cancelled) return;
        registerDeviceToken().catch(()=>{});
        const off = await onForegroundMessage((payload) => {
          const title = payload?.notification?.title || payload?.data?.title || 'GOtaps';
          const body  = payload?.notification?.body  || payload?.data?.body  || '';
          setToast({ msg: `${title} — ${body}`, type: 'info' });
        });
        unsubMsg = typeof off === 'function' ? off : (() => {});
        const audience = user.role === 'enterpriser' ? 'enterprises' : 'users';
        const acctMs = user.created_at ? new Date(user.created_at).getTime() : 0;
        unsub = subscribeToInbox(user.id, audience, acctMs, (rows) => {
          if (!cancelled) setInbox(rows);
        });
      } catch (e) {
        console.warn('Notifications subscription failed:', e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
      try { unsub(); } catch {}
      try { unsubMsg(); } catch {}
    };
  }, [authed, user?.id, user?.role, user?.created_at]);

  const showToast = useCallback((msg, type = 'success') => {
    setToast(null);
    setTimeout(() => setToast({ msg, type }), 15);
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const updateUser = useCallback((partial) => {
    setUser(prev => {
      const next = { ...prev, ...partial };
      const raw = localStorage.getItem('gotap_user');
      if (raw) {
        try {
          const stored = JSON.parse(raw);
          stored.data = next;
          localStorage.setItem('gotap_user', JSON.stringify(stored));
        } catch {}
      }
      return next;
    });
  }, []);

  const unreadCount = useMemo(
    () => inbox.reduce((n, r) => n + (r.readAt ? 0 : 1), 0),
    [inbox]
  );

  return (
    <AppContext.Provider value={{
      authed, user, toast, inbox, unreadCount,
      login, logout, updateUser, showToast, dismissToast,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
